import { PageHeader, Tabs, Tab } from '@keplerco/core';
import React, { useEffect, useState } from 'react';
import { PagePath } from '../../../../navigation/navigation.enums';
import { AssessmentProgressLayout } from './assessment-progress/assessment-progress.layout';
import { AssessmentDetailsLayout } from './assessment-details/assessment-details.layout';
import { AssessmentResultsLayout } from './assessment-results/assessment-results.layout';
import { useParams } from 'react-router';
import { useAppActions, useAppState } from '../../../../overmind';
import { PageSkeletonLoader } from '../../../../components/general/loading-state/loader-layers/page-loader-layer/page-skeleton-loader';
import { EmptyState } from '../../../../components/general/empty-state/empty-state';
import { AssessmentSearchParams, PermissionSearchParams } from '../../../../models/overmind/search-params';
import { OrganizationLevelType } from '../../../../enums';
import { extractHighestOrganizationLevel } from '../../../../library/helpers/permissions/extract-highest-organization-level';
import { FlagIcon } from '../../../../design/icons/flag.icon';
import { AnalyticsFiltersOptions, AssessmentConfiguration } from './assessment.models';
import { AssessmentResponse } from '../../../../overmind/actions';
import { AssessmentSubType } from '../../../../enums/assessment-type';

enum AssessmentAnalyticsTab {
  Details = 'Details',
  Progress = 'Progress',
  Results = 'Results',
}

export function AssessmentPage(): JSX.Element {
  const { assessmentSlug } = useParams();
  const actions = useAppActions();
  const { companyVariables, permissions, user } = useAppState();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<AssessmentAnalyticsTab>(AssessmentAnalyticsTab.Details);
  const [assessmentData, setAssessmentData] = useState<AssessmentResponse>();
  const [assessmentConfiguration, setAssessmentConfiguration] = useState<AssessmentConfiguration>({
    isSkillsFeedbackEnabled: false,
    isAssessmentFeedbackEnabled: false,
    isAnonymous: false,
  });
  const [filtersOptions, setFiltersOptions] = useState<AnalyticsFiltersOptions>({
    departmentOptions: [],
    teamOptions: [],
    roleOptions: [],
    skillOptions: [],
    countryOptions: [],
    subjectsOptions: [],
    assessorsOptions: [],
  });

  const organizationLevel: OrganizationLevelType = React.useMemo(
    () => (user?.isSystemAdmin ? OrganizationLevelType.Company : extractHighestOrganizationLevel(permissions?.analytics.organizationLevels)?.organizationLevel ?? OrganizationLevelType.Learner),
    [user?.isSystemAdmin, permissions?.analytics.organizationLevels]
  );
  const baseSearchParams: (AssessmentSearchParams & PermissionSearchParams) | undefined = React.useMemo(() => {
    if (!assessmentData || !companyVariables.slug) return undefined;

    return {
      assessmentType: assessmentData.assessmentType,
      assessmentSubType: assessmentData.assessmentSubType,
      assessmentSlug: assessmentData.slug,
      organizationLevel,
      companySlug: companyVariables.slug,
      departmentSlug: undefined,
      teamSlug: undefined,
      learnerSlug: undefined,
    };
  }, [assessmentData, organizationLevel, companyVariables.slug]);

  useEffect(() => {
    async function getAssessmentData() {
      if (!assessmentSlug || !companyVariables.slug) return;

      setIsLoading(true);
      const response = await actions.getAssessment(assessmentSlug);
      setAssessmentData(response);
      setIsLoading(false);
    }

    getAssessmentData();
  }, [assessmentSlug, companyVariables.slug!]);

  useEffect(() => {
    async function getFiltersItems() {
      if (!assessmentSlug || !companyVariables.slug) return;

      const [departmentsResponse, teamsResponse, rolesResponse, skillsResponse, countriesResponse] = await Promise.all([
        actions.getAssessmentAnalyticsDepartments(assessmentSlug),
        actions.getAssessmentAnalyticsTeams(assessmentSlug),
        actions.getAssessmentAnalyticsRoles(assessmentSlug),
        actions.getAssessmentAnalyticsSkills(assessmentSlug),
        actions.getCountries(), // TODO: does this need an assessment analytics API?
      ]);

      setFiltersOptions({
        departmentOptions: departmentsResponse?.map(department => ({ label: department.name, value: department.slug! })) ?? [],
        teamOptions: teamsResponse?.map(team => ({ label: team.name, value: team.slug! })) ?? [],
        roleOptions: rolesResponse?.map(role => ({ label: role.name, value: role.slug! })) ?? [],
        skillOptions: skillsResponse?.map(skill => ({ label: skill.name, value: skill.slug! })) ?? [],
        countryOptions:
          countriesResponse?.map(country => ({
            label: country.name,
            value: String(country.id),
            icon: <FlagIcon isoCode={country.iso} />,
          })) ?? [],
      });

      if (assessmentData?.assessmentSubType === AssessmentSubType.RatingAssessment) {
        const subjects = new Map<string, { name: string; slug: string }>();
        const assessors = new Map<string, { name: string; slug: string }>();

        const peerResponse = await actions.getPeerAssessment({ assessmentSlug: assessmentSlug });
        setAssessmentConfiguration({
          isAnonymous: !!peerResponse?.isAnonymous,
          isSkillsFeedbackEnabled: !!peerResponse?.isSkillsFeedbackEnabled,
          isAssessmentFeedbackEnabled: !!peerResponse?.isAssessmentFeedbackEnabled,
        });

        if (!peerResponse?.isAnonymous) {
          peerResponse?.assignees.forEach(assignee => {
            subjects.set(assignee.learnerSlug, { name: assignee.fullName, slug: assignee.learnerSlug });
            assignee.reviewers.forEach(reviewer => {
              assessors.set(reviewer.learnerSlug, { name: reviewer.fullName, slug: reviewer.learnerSlug });
            });
          });
        }

        setFiltersOptions(currentState => ({
          ...currentState,
          subjectsOptions: Array.from(subjects.values()).map(subject => ({ value: subject.slug, label: subject.name })),
          assessorsOptions: Array.from(assessors.values()).map(assessor => ({ value: assessor.slug, label: assessor.name })),
        }));
      }
    }

    getFiltersItems();
  }, [assessmentSlug, companyVariables.slug, assessmentData?.assessmentSubType]);

  if (isLoading) return <PageSkeletonLoader />;

  return (
    <div className="wrapper stack" style={{ gap: 24 }}>
      <PageHeader
        title={assessmentData?.name ?? 'Assessment'}
        breadcrumbs={[
          { name: `Analytics`, url: PagePath.analysisBase },
          { name: `Assessments`, url: `${PagePath.analyticsBase}${PagePath.analyticsAssessments.replace(':companySlug', companyVariables.slug!)}` },
          { name: `${assessmentData?.name ?? 'Assessment'}`, url: location.pathname },
        ]}
      />

      <div style={{ width: '100%' }}>
        <Tabs static hideMobile={false}>
          <Tab id={AssessmentAnalyticsTab.Details} name={AssessmentAnalyticsTab.Details} active={activeTab === AssessmentAnalyticsTab.Details} onClick={() => setActiveTab(AssessmentAnalyticsTab.Details)} />
          <Tab id={AssessmentAnalyticsTab.Progress} name={AssessmentAnalyticsTab.Progress} active={activeTab === AssessmentAnalyticsTab.Progress} onClick={() => setActiveTab(AssessmentAnalyticsTab.Progress)} />
          <Tab id={AssessmentAnalyticsTab.Results} name={AssessmentAnalyticsTab.Results} active={activeTab === AssessmentAnalyticsTab.Results} onClick={() => setActiveTab(AssessmentAnalyticsTab.Results)} />
        </Tabs>
      </div>

      {!!assessmentData && !!baseSearchParams ? (
        <React.Fragment>
          {activeTab === AssessmentAnalyticsTab.Details && <AssessmentDetailsLayout assessment={assessmentData} baseSearchParams={baseSearchParams} />}

          {activeTab === AssessmentAnalyticsTab.Progress && (
            <AssessmentProgressLayout baseSearchParams={baseSearchParams} departmentOptions={filtersOptions.departmentOptions} teamOptions={filtersOptions.teamOptions} roleOptions={filtersOptions.roleOptions} countryOptions={filtersOptions.countryOptions} />
          )}

          {activeTab === AssessmentAnalyticsTab.Results && <AssessmentResultsLayout assessmentConfiguration={assessmentConfiguration} filtersOptions={filtersOptions} baseSearchParams={baseSearchParams} />}
        </React.Fragment>
      ) : (
        <EmptyState />
      )}
    </div>
  );
}
